import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {number} */
  #lockedScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupOffsiderDrawerControl();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.querySelector('.offsider-menu-panel__nav')?.removeEventListener(
      'click',
      this.#onOffsiderPanelLinkClick
    );
    this.#unlockBodyScroll();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * @returns {boolean}
   */
  get #isOffsiderDrawer() {
    return this.hasAttribute('data-offsider-menu-drawer');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Evita el toggle nativo de `<details>` (provoca scroll-lock global y salto al inicio).
   */
  #setupOffsiderDrawerControl() {
    if (!this.#isOffsiderDrawer) return;

    const summary = this.querySelector('summary');
    summary?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
      },
      { capture: true }
    );

    this.querySelector('.offsider-menu-panel__nav')?.addEventListener(
      'click',
      this.#onOffsiderPanelLinkClick
    );
  }

  /**
   * Cierra el panel al pulsar un enlace. En anclas de la misma página, hace scroll tras desbloquear el body.
   * @param {MouseEvent} event
   */
  #onOffsiderPanelLinkClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a.offsider-menu-panel__link');
    if (!link || !this.isOpen) return;

    const hash = this.#getSamePageHash(link);

    if (hash) {
      event.preventDefault();
    }

    this.close();

    if (!hash) return;

    const target = document.querySelector(hash);
    if (!target) return;

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', hash);
    });
  };

  /**
   * @param {HTMLAnchorElement} link
   * @returns {string | null}
   */
  #getSamePageHash(link) {
    const rawHref = link.getAttribute('href');
    if (!rawHref || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) return null;

    try {
      const url = new URL(link.href, window.location.href);
      const normalizePath = (pathname) => pathname.replace(/\/$/, '') || '/';

      if (url.origin !== window.location.origin) return null;
      if (normalizePath(url.pathname) !== normalizePath(window.location.pathname)) return null;
      if (!url.hash || url.hash === '#') return null;

      return url.hash;
    } catch {
      return null;
    }
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event instanceof Event) {
      event.preventDefault();
    }

    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    if (event instanceof Event) {
      event.preventDefault();
    }

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    details.setAttribute('open', '');

    if (details.classList.contains('menu-drawer-container--offsider')) {
      this.#lockBodyScroll();
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      if (
        window.matchMedia('(min-width: 990px)').matches &&
        !details.classList.contains('menu-drawer-container--offsider')
      ) {
        details.classList.add('menu-search-open');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector(
        '.menu-drawer, .menu-drawer__submenu, .menu-drawer--offsider-panel'
      );
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Bloquea scroll de fondo sin saltar al inicio de la página.
   */
  #lockBodyScroll() {
    if (document.body.classList.contains('offsider-menu-scroll-locked')) return;

    this.#lockedScrollY = window.scrollY;
    document.body.classList.add('offsider-menu-scroll-locked');
    document.body.style.setProperty('--offsider-menu-scroll-lock-y', `-${this.#lockedScrollY}px`);
  }

  /**
   * Restaura la posición de scroll al cerrar el panel Menú.
   */
  #unlockBodyScroll() {
    if (!document.body.classList.contains('offsider-menu-scroll-locked')) return;

    const scrollY = this.#lockedScrollY;
    document.body.classList.remove('offsider-menu-scroll-locked');
    document.body.style.removeProperty('--offsider-menu-scroll-lock-y');
    window.scrollTo(0, scrollY);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    details.classList.remove('menu-search-open');

    if (details.classList.contains('menu-drawer-container--offsider')) {
      this.#unlockBodyScroll();
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector(
      '.menu-drawer, .menu-drawer__submenu, .menu-drawer--offsider-panel'
    );

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
